'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "IconComponent", "disabled", "iconCount", "tabIndex", "value", "readOnly", "showPercentage", "id", "onHoveredValueChange"];
import I18n from 'I18n';
import range from '../../utils/underscore/range';
import PropTypes from 'prop-types';
import { Component } from 'react';
import partial from 'react-utils/partial';
import { callIfPossible } from '../../core/Functions';
import SyntheticEvent from '../../core/SyntheticEvent';
import { getTabIndex } from '../../utils/TabIndex';
import UIIconToggle from '../UIIconToggle';
import Controllable from '../../decorators/Controllable';
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
class UIAbstractRatingInput extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      focusValue: null
    };
    this.handleChange = value => {
      if (this.props.disabled || this.props.readOnly) {
        return;
      }
      callIfPossible(this.props.onChange, SyntheticEvent(value));
    };
    this.handleKeyDown = evt => {
      const {
        iconCount,
        value
      } = this.props;
      let nextValue = value || 0;
      if (evt.key === 'ArrowLeft') {
        nextValue = Math.floor(nextValue) <= 0 ? 0 : Math.ceil(nextValue) - 1;
      } else if (evt.key === 'ArrowRight') {
        nextValue = Math.ceil(nextValue) >= iconCount ? iconCount : Math.floor(nextValue) + 1;
      }
      this.handleChange(nextValue);
    };
    this.handleHover = value => {
      if (this.props.disabled || this.props.readOnly) {
        return;
      }
      this.setState({
        focusValue: value
      });
    };
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevState.focusValue !== this.state.focusValue) {
      callIfPossible(this.props.onHoveredValueChange, SyntheticEvent(this.state.focusValue));
    }
  }
  getPercentageFilled(index, value) {
    if (value == null) {
      return 0;
    }
    if (this.state.focusValue) {
      if (index <= this.state.focusValue) {
        return 100;
      }
      return 0;
    }
    if (index <= Math.floor(value)) {
      return 100;
    }
    if (index > Math.ceil(value)) {
      return 0;
    }
    return Math.round(value % 1 * 100);
  }
  getIsHighlighted(index, value) {
    if (this.state.focusValue !== null) {
      return index <= this.state.focusValue;
    }
    return index <= value;
  }
  render() {
    const _this = this,
      {
        props: {
          className,
          IconComponent,
          disabled,
          iconCount,
          tabIndex,
          value,
          readOnly,
          showPercentage,
          id
        },
        state: {
          focusValue
        }
      } = _this,
      rest = _objectWithoutPropertiesLoose(_this.props, _excluded);
    return /*#__PURE__*/_jsx("div", {
      id: id,
      "aria-disabled": disabled,
      "aria-valuemax": iconCount,
      "aria-valuemin": "0",
      "aria-valuenow": value || 0,
      "aria-valuetext": I18n.text('salesUI.UIIconRating.ariaValueText', {
        value: value || 0,
        maxValue: iconCount
      }),
      className: className,
      role: "slider",
      tabIndex: getTabIndex(disabled, tabIndex),
      onKeyDown: this.handleKeyDown,
      children: range(1, iconCount + 1).map(i => /*#__PURE__*/_createElement(UIIconToggle, Object.assign({}, rest, {
        key: i,
        index: i,
        onChange: partial(this.handleChange, i),
        onMouseEnter: partial(this.handleHover, i),
        onMouseLeave: partial(this.handleHover, null),
        "aria-label": "",
        checked: this.getIsHighlighted(i, value),
        readOnly: readOnly,
        percentFilled: showPercentage ? this.getPercentageFilled(i, value) : null,
        tabIndex: -1,
        IconComponent: IconComponent,
        active: value === i,
        disabled: disabled,
        focus: i === focusValue,
        hover: i <= focusValue
      })))
    });
  }
}
UIAbstractRatingInput.displayName = 'UIAbstractRatingInput';
UIAbstractRatingInput.defaultProps = {
  iconCount: 5,
  showPercentage: false
};
UIAbstractRatingInput.propTypes = {
  IconComponent: PropTypes.elementType,
  disabled: PropTypes.bool,
  iconCount: PropTypes.number,
  onChange: PropTypes.func,
  onHoveredValueChange: PropTypes.func,
  readOnly: PropTypes.bool,
  showPercentage: PropTypes.bool.isRequired,
  value: PropTypes.number
};
export default Controllable(UIAbstractRatingInput);